import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { useModal } from '@/hooks/useModal';
import { useDeleteFundRelationship } from '@/hooks/react-query/useInvestors';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { InvestorDetail } from '@/types/investor';

interface RemoveInvestorModalProps {
  open: boolean;
  fundId: string;
  investor: InvestorDetail | null;
  onClose: () => void;
  onRemoved?: () => void;
}

export function RemoveInvestorModal({
  open,
  fundId,
  investor,
  onClose,
  onRemoved,
}: RemoveInvestorModalProps) {
  const [removeValidation, setRemoveValidation] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);

  const deleteFundRelationshipMutation = useDeleteFundRelationship();
  const { modalProps } = useModal({
    isOpen: open,
    onClose,
  });

  const handleConfirmRemove = async () => {
    if (!investor || removeValidation !== 'DELETE') {
      return;
    }

    const relationship = investor.fund_relationships.find(
      (candidate) => candidate.fund_id === fundId
    );

    if (!relationship) {
      return;
    }

    setRemoveError(null);

    try {
      await deleteFundRelationshipMutation.mutateAsync(relationship.id);
      onClose();
      onRemoved?.();
    } catch (error) {
      const errorMessage =
        (error as { message?: string })?.message ||
        'Failed to remove investor from fund';
      setRemoveError(errorMessage);

      console.error('Failed to remove investor - Error object:', error);
      console.error('Error type:', error?.constructor?.name);
      console.error('Error message:', (error as Error)?.message);
    }
  };

  const isRemoving = deleteFundRelationshipMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md" {...modalProps}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Remove Investor
          </DialogTitle>
          <DialogDescription className="sr-only">
            Confirm removal of an investor relationship from this fund.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {removeError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <div className="ml-2">
                <p className="font-medium">Cannot Remove Investor</p>
                <p className="mt-1 text-sm">{removeError}</p>
              </div>
            </Alert>
          )}

          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              <strong>Warning:</strong> This removes{' '}
              <span className="font-medium">{investor?.name}</span> from this
              fund and cannot be undone.
            </p>
            <p className="mt-2 text-sm text-destructive">
              If the investor appears on an issued capital call or a completed
              distribution, it is marked as exited instead: it drops out of
              draft and future calls, while its issued notices, payments and
              drawdown totals stay unchanged.
            </p>
          </div>

          <div>
            <Label htmlFor="validation">
              Type <code className="rounded bg-muted px-1">DELETE</code> to
              confirm
            </Label>
            <Input
              value={removeValidation}
              onChange={(event) => setRemoveValidation(event.target.value)}
              placeholder="Type DELETE to confirm"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isRemoving}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmRemove}
            disabled={isRemoving || removeValidation !== 'DELETE'}
          >
            {isRemoving ? 'Removing...' : 'Remove Investor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
